/**
 * Session hygiene — the rules that keep one browser from holding two
 * identities at once.
 *
 * Why this module exists (owner incident, 2026-10-19, dev.denvstack.com): a
 * session signed in as a non-super-admin rendered the SUPER-ADMIN's identity
 * in the SPA navbar after a plain navigation, with no credential entered. The
 * SPA's own navbar changing identity means the restoration happened in this
 * app, not in the server-rendered dashboard pages.
 *
 * Everything here is pure (no `@/` imports, no Vue, no axios) so it can be
 * driven directly by `node --test`. Three jobs:
 *
 *   1. Name every artefact a session leaves behind — web-storage keys AND
 *      the cookie names the same-origin platform-dashboard pages accept —
 *      so a login or a logout can clear the complete set rather than the
 *      subset this app happens to write.
 *   2. Build the cookie strings that actually delete a cookie. A cookie is
 *      only removed by an expiry that matches the attributes it was set
 *      with, so a host-only expiry leaves a `Domain=`-scoped twin alive.
 *   3. Compare identities. A token refresh, a `/me` merge or a rehydrated
 *      persisted store must never be allowed to change WHO the session is.
 *
 * Never log or print a token value from here.
 */

/**
 * Cookie names the platform-dashboard `_extract_token` accepts on this
 * origin. This app writes only the first; the dashboard's own sign-in bridge
 * has written the second, and `token` is historical. A login or logout that
 * clears only its own name leaves the dashboard resolving a viewer from
 * somebody else's leftovers — which is how two identities coexisted on one
 * origin. Dashboard 0.27.1 now fails closed when two of these disagree, so
 * leaving a second one behind reads to the customer as "signed out".
 *
 * Do NOT rename `cs_access_token` here: converging both apps on one
 * canonical name is a coordinated cross-repo change (see CLAUDE.md
 * follow-up). This list is about CLEARING, not about which one we write.
 */
export const ACCEPTED_COOKIE_NAMES = Object.freeze(['cs_access_token', 'access_token', 'token'])

/** The cookie this app writes. The other accepted names are cleared, never written. */
export const SESSION_COOKIE_NAME = ACCEPTED_COOKIE_NAMES[0]

/**
 * Every web-storage key that is part of a session, in BOTH storages.
 * `vuex-persist` is in the list because the persisted auth module carries the
 * user object the navbar renders and the tokens `initializeAuth` refreshes —
 * it is a credential, not a cache. `cs:remember` is handled separately
 * because a login needs to set it after the clear.
 */
/**
 * The storage keys THIS app owns. Namespaced (2026-10-22) because the origin
 * is shared: on dev.denvstack.com the cluster-api console
 * (`static/js/session_token_bootstrap.js`) mirrors ITS session into
 * localStorage `access_token` / `token` / `authToken` on every console page
 * load. While this app also read `access_token`, logging into the console as
 * one account in another tab silently handed that account's token to this
 * app's next page load — and the docs portal's bridge copied the same key
 * into its cookie, so /docs rendered "signed in as <console user>" while the
 * dashboard still showed the customer. A key no other app on the origin
 * writes is the only cure; the old names are read once for migration
 * (token-storage.js::migrateLegacySessionKeys) and cleared on sign-out.
 */
export const ACCESS_TOKEN_KEY = 'cs:access_token'
export const REFRESH_TOKEN_KEY = 'cs:refresh_token'
export const TOKEN_VALID_SECONDS_KEY = 'cs:token_valid_seconds'

/** The un-namespaced names this app used before 2026-10-22, plus SSO orphans. */
export const LEGACY_SESSION_STORAGE_KEYS = Object.freeze([
  'access_token',
  'refresh_token',
  'token_valid_seconds',
  'authToken',
  'token'
])

export const SESSION_STORAGE_KEYS = Object.freeze([
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_VALID_SECONDS_KEY,
  'vuex-persist',
  // Cleared on sign-out so a migrated session cannot linger under its old
  // name, and so SSO-landing orphans (`authToken`, `token`) go too.
  ...LEGACY_SESSION_STORAGE_KEYS
])

export const REMEMBER_FLAG_KEY = 'cs:remember'

const EPOCH = 'Thu, 01 Jan 1970 00:00:00 GMT'

/**
 * The `Domain` values a cookie on `host` could have been set with, from most
 * to least specific: host-only (no Domain attribute at all), `Domain=<host>`,
 * and each registrable-looking parent (`.example.com`). A host-only expiry
 * does NOT remove a `Domain=`-scoped cookie of the same name, so a delete has
 * to try every variant.
 *
 * The last label pair is kept (`example.com`) and single-label hosts
 * (`localhost`) and IP literals get no parent variants.
 */
export function cookieDomainVariants(host) {
  const clean = String(host || '').trim().toLowerCase().replace(/\.$/, '')
  if (!clean || /^\[|^\d+\.\d+\.\d+\.\d+$/.test(clean)) return [null]
  const labels = clean.split('.').filter(Boolean)
  if (labels.length < 2) return [null]
  const variants = [null, clean]
  for (let i = 1; i <= labels.length - 2; i++) {
    variants.push(`.${labels.slice(i).join('.')}`)
  }
  return variants
}

/**
 * Cookie strings that delete `name` on `host` whichever Domain attribute it
 * was set with. Path=/ only: every writer on this origin uses it.
 */
export function cookieDeletions(name, { host = '', secure = false } = {}) {
  const tail = `; Expires=${EPOCH}; Max-Age=0; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
  return cookieDomainVariants(host).map(
    (domain) => `${name}=${tail}${domain ? `; Domain=${domain}` : ''}`
  )
}

/**
 * Cookie strings that delete every accepted name, optionally keeping one
 * (the one we are about to write). Used by both the write path (so exactly
 * one accepted cookie exists) and the clear path.
 */
export function allCookieDeletions({ host = '', secure = false, except = null } = {}) {
  const out = []
  for (const name of ACCEPTED_COOKIE_NAMES) {
    if (name === except) continue
    out.push(...cookieDeletions(name, { host, secure }))
  }
  return out
}

// ── identity ───────────────────────────────────────────────────────────────

/** Decoded JWT payload, or null when the value is not a decodable JWT. */
export function decodeJwtPayload(token) {
  try {
    const part = String(token).split('.')[1]
    if (!part) return null
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const payload = JSON.parse(atob(padded))
    return payload && typeof payload === 'object' ? payload : null
  } catch {
    return null
  }
}

function identity(id, name) {
  const normId = id === 0 || id ? String(id) : null
  const normName = name ? String(name).trim().toLowerCase() : null
  if (!normId && !normName) return null
  return { id: normId, name: normName }
}

/** Who a JWT is for: `{id, name}` (either may be null), or null if undecodable. */
export function tokenIdentity(token) {
  const p = decodeJwtPayload(token)
  if (!p) return null
  return identity(p.user_id ?? p.userId ?? p.uid ?? null, p.username || p.sub || p.email || null)
}

/** Who a store/`/me` user object is for. */
export function userIdentity(user) {
  if (!user || typeof user !== 'object') return null
  return identity(
    user.user_id ?? user.userId ?? user.id ?? null,
    user.username || user.email || user.sub || null
  )
}

/**
 * true / false when the two identities can be compared, null when they
 * cannot (one side undecodable, or no field in common). Callers decide what
 * an unknown means; this module never pretends to know.
 */
export function sameIdentity(a, b) {
  if (!a || !b) return null
  if (a.id && b.id) return a.id === b.id
  if (a.name && b.name) return a.name === b.name
  return null
}

/**
 * May this freshly minted access token be adopted by the current session?
 *
 * The 401 interceptor refreshes with `store.state.auth.refreshToken ||
 * readToken('refresh_token')`. The storage fallback is not bound to the
 * signed-in user, so a refresh token left behind by a previous (possibly
 * privileged) session can mint an access token for SOMEBODY ELSE — which
 * `SET_AUTH_DATA` would then write into storage AND into the same-origin
 * session cookie. This is the gate on that.
 *
 * See verdict() below for exactly when a mismatch is refused. `reason` is
 * for a log line — never the token.
 */
export function acceptRefreshedToken({ sessionToken, sessionUser, newToken } = {}) {
  if (!newToken) return { ok: false, reason: 'no_token' }
  return verdict(tokenIdentity(newToken), [tokenIdentity(sessionToken), userIdentity(sessionUser)])
}

/**
 * `fresh` is who the incoming thing is for; `known` are the things the
 * session knows about itself (its access token, its user object), either of
 * which may be unknowable.
 *
 * Accept as soon as ONE comparison agrees. Refuse only when nothing agrees
 * and something disagrees. Accept when nothing can be compared at all.
 *
 * The asymmetry is deliberate: a false refusal signs a working session out
 * (the store's user object is assembled from a login payload and can carry a
 * login alias rather than the canonical username, so a name-only comparison
 * can disagree about the same person), while the escalation this guards
 * against has BOTH sides of the session pointing at the lower-privileged
 * account and the incoming thing pointing at the privileged one — so it
 * disagrees twice and agrees nowhere.
 */
function verdict(fresh, known) {
  const results = known.map((k) => sameIdentity(k, fresh))
  if (results.some((r) => r === true)) return { ok: true, reason: null }
  if (results.some((r) => r === false)) return { ok: false, reason: 'identity_mismatch' }
  return { ok: true, reason: null }
}

/**
 * May this `/me` payload be merged into the session's user object?
 *
 * `MERGE_USER` spreads the payload over the stored user, so every field it
 * carries — email, user_id, is_admin, is_super_admin — is replaced. One
 * `/me` read made with a stale privileged token therefore rewrites who the
 * navbar says you are and unlocks the super-admin entries. A `/me` call may
 * refresh your details; it may never change your identity.
 */
export function acceptMeIdentity({ sessionToken, sessionUser, me } = {}) {
  if (!me) return { ok: false, reason: 'no_payload' }
  return verdict(userIdentity(me), [userIdentity(sessionUser), tokenIdentity(sessionToken)])
}

/**
 * Do the two storages disagree about a session key?
 *
 * `readToken` is `localStorage.getItem(k) || sessionStorage.getItem(k)` and
 * the vuex-persist adapter reads the same way, so localStorage always wins a
 * disagreement — regardless of which copy is newer or less privileged. A
 * disagreement is not something to resolve by preference; it means the origin
 * holds two sessions and neither can be trusted. Same reasoning as the
 * dashboard's 0.27.1 cookie fix.
 */
export function storagesDisagree(localValue, sessionValue) {
  return Boolean(localValue) && Boolean(sessionValue) && localValue !== sessionValue
}

/**
 * App start: does the session the store just rehydrated agree with the token
 * sitting in web storage?
 *
 * Two independent slots decide what the user sees: the persisted vuex blob
 * (the navbar's identity, and the tokens `initializeAuth` refreshes) and the
 * `access_token` key (what the request interceptors fall back to and what
 * gets mirrored into the dashboard's cookie). They are written by the same
 * code but routed by a single global remember flag, so a second tab can leave
 * them pointing at different sessions — and then the SPA renders one identity
 * while every request is made as another.
 *
 *   'ok'    — coherent (including fully anonymous).
 *   'clear' — incoherent; wipe the session and render signed out. Signing in
 *             again is cheap, and it is the only honest answer: we cannot
 *             know which of the two the user meant.
 *
 * A stored token that is MISSING while the store has one is not a conflict:
 * `api.js` drops the access token on a 401 and leaves the refresh flow to
 * recover it.
 */
export function bootSessionVerdict({ storedToken, stateToken, stateUser } = {}) {
  if (!storedToken) return 'ok'
  // A token on disk with no session in the store is a GHOST: the navbar shows
  // nobody while every request interceptor still falls back to that token. A
  // remembered session always persists its blob alongside the token, so this
  // only happens when one of the two was lost or cleared behind the other.
  if (!stateToken) return 'clear'
  if (stateToken !== storedToken) return 'clear'
  // Same token, but the persisted user is a different account: the navbar
  // would render somebody the token is not for. Compared on user_id only —
  // a name-only disagreement is more likely a login alias than two sessions,
  // and a false clear signs a working session out.
  const persisted = userIdentity(stateUser)
  const bearer = tokenIdentity(storedToken)
  if (persisted?.id && bearer?.id && persisted.id !== bearer.id) return 'clear'
  return 'ok'
}
